import { useEffect, useMemo, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ReactTable, Spinner, SkeletonLine, Checkbox, Button, DateRangePicker, Tooltip } from '@crazyegginc/hatch';
import { Modal } from '/src/contexts/modal';
import { useAtom, useSetAtom, atom } from 'jotai';
import { abTestTypes } from '/src/features/ab-testing/constants';

import { LazyImg } from '/src/components/LazyImg';
import { DateDisplay } from '/src/components/date-time/DateDisplay';
import { SelectionProvider } from '/src/contexts/selection';
import { displayUrl } from '/src/utils/url';
import { CreateAbTestButton } from '../components/CreateAbTestButton';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { DashboardPage } from '/src/components/Page';
import { LinkWithParams } from '/src/components/LinkWithParams';
import { Breadcrumb, BreadcrumbItem } from '/src/components/Breadcrumb';

import {
  DEFAULT_DATE_RANGE_POLICIES,
  MAX_ALLOWABLE_DATE_RANGES,
  SORT_ORDER_TYPES,
  TIMEZONES,
} from '/src/features/_global/constants';
import { camelToSnake, snakeToCamel } from '/src/utils/string';
import { JSON_DATE_RANGE_ADAPTER } from '/src/utils/date-range-adapters';

import {
  useModal,
  useAuthContext,
  useCanonicalDateRangeParam,
  useNotifications,
  usePermissions,
  useSelection,
  useQueryParams,
} from '/src/hooks';

import { AbTestDetailModal } from '../components/modals/AbTestDetailModal';
import { NotesModal } from '../components/modals/NotesModal';
import { AbTestActionButton } from '../components/AbTestActionButton';
import { SummaryRow, SummaryCard, SummaryGoalSelector } from '../components/SummaryRow';
import {
  DistributionChart,
  abTestDistributionDatasetAtom,
} from '/src/features/ab-testing/components/DistributionChart';
import { CreatedByText } from '/src/components/CreatedByText';

import { AddSplitVariant } from '../components/modals/AddSplitVariant';
import { EditSplitVariant } from '../components/modals/EditSplitVariant';
import { getAbTestFeatureStartedAt } from '../date-range';
// import { AbTestRenameModal } from '../components/modals/AbTestRenameModal';

import { formatCurrency, isProduction, isSharedResourceSubscriptionError } from '/src/utils';
import { RateDelta, mergeAbTestResultPageData } from '../utils';
import { getAbTestValueCurrency, valueDisplayCurrency, valueDisplayWorth } from '../value-utils';

import { abTestResultCountsQuery, abTestResultDetailQuery } from '../queries';
import { goalRefListQuery } from '/src/features/goals/queries';
import { FEATURES } from '/src/features/_global/constants';
import { AB_TEST_STATUSES } from '../constants';

import { ReactComponent as DescriptionIcon } from '@crazyegginc/hatch/dist/images/icon-page-w-text-outline.svg';
import { ReactComponent as NotesIcon } from '@crazyegginc/hatch/dist/images/icon-form.svg';
import { ReactComponent as NotesAddedIcon } from '@crazyegginc/hatch/dist/images/icon-form-tick.svg';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { ReactComponent as InfoIcon } from '@crazyegginc/hatch/dist/images/icon-info-circle-outline.svg';
import { VariantActionButton } from '../components/VariantActionButton';
import { ReactComponent as EyeIcon } from '@crazyegginc/hatch/dist/images/icon-watched-outine.svg';
import { ReactComponent as ImgPlaceholderIcon } from '@crazyegginc/hatch/dist/images/illustration-thumbnail-placeholder.svg';
import { AbTestNotFoundWall } from '../components/paywalls/NotFoundWall';
import { atomWithDefault, useResetAtom } from 'jotai/utils';
import { AppLink } from '/src/features/_global/components/AppLink';
import { getAbTestVariantColor } from '../variant-colors';

const isProd = isProduction();

const abTestLocationSearchAtom = atom(typeof window !== 'undefined' ? window.location.search : '');

function normalizeGoalId(value) {
  const goalId = Number(value);
  return Number.isInteger(goalId) && goalId > 0 ? goalId : null;
}
const selectedGoalIdAtom = atomWithDefault((get) => {
  const params = new URLSearchParams(get(abTestLocationSearchAtom));
  return normalizeGoalId(params.get('goal'));
});

function VariantScreenshotModal({ variant }) {
  const modal = useModal();
  const notifications = useNotifications();

  return (
    <Modal dialogClassName="!w-auto max-w-[90vw] max-h-[90vh] pt-10">
      <img
        src={variant.screenshotUrl}
        alt="screenshot"
        onError={() => {
          notifications.info({
            title: 'Screenshot still processing',
            content: `This variant's screenshot is still being processed. Please check back later.`,
            timeout: 3000,
          });
          modal.close();
        }}
      />
    </Modal>
  );
}

const enableRetiredVariantsAtom = atom(false);

export function AbTestsResult() {
  return (
    <SelectionProvider>
      <AbTestResults />
    </SelectionProvider>
  );
}

function AbTestResults() {
  const { isSharing } = useAuthContext();
  const { setSelection } = useSelection();
  const modal = useModal();
  const perfMetricsStarted = useRef(false);
  const variantsTableRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const notifications = useNotifications();
  const [dataset, setDataset] = useAtom(abTestDistributionDatasetAtom);
  const [selectedGoalId, setSelectedGoalId] = useAtom(selectedGoalIdAtom);
  const setAbTestLocationSearch = useSetAtom(abTestLocationSearchAtom);
  const [showRetiredVariants] = useAtom(enableRetiredVariantsAtom);
  const resetDataset = useResetAtom(abTestDistributionDatasetAtom);
  const resetSelectedGoal = useResetAtom(selectedGoalIdAtom);
  const { get: paramGet, set: paramSet, remove: paramRemove, hasRaw: paramHasRaw } = useQueryParams();
  const order = paramGet('order') || 'NAME';
  const sort = paramGet('sort') || 'ASC';

  const permissions = usePermissions();

  const setGoal = useCallback(
    (goal) => {
      const goalId = Number(goal?.id);

      if (!goalId) return;

      setSelectedGoalId(goalId);
      paramSet('goal', goalId);
    },
    [setSelectedGoalId, paramSet],
  );

  useEffect(() => {
    setAbTestLocationSearch(location.search);
    resetSelectedGoal();
  }, [location.search, resetSelectedGoal, setAbTestLocationSearch]);

  useEffect(() => {
    resetDataset();
  }, [resetDataset]);

  const {
    data: detailResult,
    isFetching: isFetchingDetail,
    isInitialLoading: isInitialLoadingDetail,
    error: detailError,
  } = useQuery({
    ...abTestResultDetailQuery({
      id: Number(id),
    }),
    enabled: !!id,
    cacheTime: 0,
    networkMode: 'always',
  });

  const detailData = detailResult?.abTestResultDetail ?? null;
  const featureStartedAt = getAbTestFeatureStartedAt(detailData) ?? undefined;
  const featureEndedAt = detailData?.stoppedAt ?? undefined;
  const { resolvedDateRange } = useCanonicalDateRangeParam({
    featureStartedAt,
    featureEndedAt,
    maxAllowableRange: MAX_ALLOWABLE_DATE_RANGES.DEFAULT,
    defaultDateRangeOptions: {
      endedDefault: DEFAULT_DATE_RANGE_POLICIES.LIFETIME,
    },
  });
  const convertedDateRange = resolvedDateRange?.convertedDateRange ?? null;
  const canonicalDetailGoalId = useMemo(
    () => normalizeGoalId(detailData?.selectedGoal?.id ?? detailData?.goal?.id),
    [detailData?.goal?.id, detailData?.selectedGoal?.id],
  );

  const { data: goalsData, isFetching: isFetchingGoals } = useQuery({
    ...goalRefListQuery({
      siteId: isSharing ? null : Number(detailData?.siteId),
    }),
    enabled: isSharing || !!detailData?.siteId,
  });

  const goalsList = useMemo(() => goalsData?.goalRefList ?? [], [goalsData?.goalRefList]);
  const hasLoadedGoalsList = Boolean(goalsData);
  const selectedGoalIdExistsInGoalsList = useMemo(
    () => goalsList.some((goal) => normalizeGoalId(goal.id) === selectedGoalId),
    [goalsList, selectedGoalId],
  );
  const effectiveSelectedGoalId = useMemo(() => {
    if (selectedGoalId == null) return canonicalDetailGoalId;
    if (selectedGoalId === canonicalDetailGoalId) {
      return selectedGoalId;
    }
    if (!hasLoadedGoalsList) {
      return selectedGoalId;
    }
    if (selectedGoalIdExistsInGoalsList) {
      return selectedGoalId;
    }
    return canonicalDetailGoalId;
  }, [canonicalDetailGoalId, hasLoadedGoalsList, selectedGoalId, selectedGoalIdExistsInGoalsList]);
  const countsEnabled = Boolean(
    id &&
      detailData &&
      effectiveSelectedGoalId &&
      Number.isFinite(convertedDateRange?.startDate) &&
      Number.isFinite(convertedDateRange?.endDate),
  );

  const {
    data: countsResult,
    isFetching: isFetchingCounts,
    error: countsError,
    refetch: refetchCounts,
  } = useQuery({
    ...abTestResultCountsQuery({
      id: Number(id),
      goalId: effectiveSelectedGoalId,
      startDate: convertedDateRange?.startDate,
      endDate: convertedDateRange?.endDate,
      order,
      sort,
    }),
    enabled: countsEnabled,
    keepPreviousData: true,
    cacheTime: 0,
    networkMode: 'always',
  });

  const countsData = countsResult?.abTestResultCounts ?? null;

  useEffect(() => {
    if (!paramHasRaw('goal')) return;

    if (selectedGoalId == null) {
      paramRemove('goal');
      return;
    }

    if (selectedGoalId === effectiveSelectedGoalId) return;
    if (!detailData) return;
    if (selectedGoalId !== canonicalDetailGoalId && isFetchingGoals) return;

    if (effectiveSelectedGoalId) {
      setSelectedGoalId(effectiveSelectedGoalId);
      paramSet('goal', effectiveSelectedGoalId);
      return;
    }

    setSelectedGoalId(null);
    paramRemove('goal');
  }, [
    canonicalDetailGoalId,
    detailData,
    effectiveSelectedGoalId,
    isFetchingGoals,
    paramHasRaw,
    paramRemove,
    paramSet,
    selectedGoalId,
    setSelectedGoalId,
  ]);

  const selectedGoal = useMemo(() => {
    return (
      goalsList.find((goal) => Number(goal.id) === effectiveSelectedGoalId) ??
      countsData?.selectedGoal ??
      detailData?.selectedGoal ??
      detailData?.goal ??
      null
    );
  }, [countsData?.selectedGoal, detailData?.goal, detailData?.selectedGoal, effectiveSelectedGoalId, goalsList]);

  const resultData = useMemo(
    () =>
      mergeAbTestResultPageData(detailData, countsData, {
        metricsUnavailable: Boolean(countsError),
      }),
    [countsData, countsError, detailData],
  );

  const filteredVariants = useMemo(() => {
    return (resultData?.variants ?? []).filter((v) => showRetiredVariants || !v.isRetired);
  }, [resultData?.variants, showRetiredVariants]);

  useEffect(() => {
    setSelection(filteredVariants);
  }, [filteredVariants, setSelection]);

  useEffect(() => {
    if (window.CE2?.timing && isProd && !perfMetricsStarted.current) {
      window.CE2.timing.start('ab_test_result_page');
      perfMetricsStarted.current = true;
    }
  }, []);

  useEffect(() => {
    if (
      window.CE2?.timing &&
      isProd &&
      perfMetricsStarted.current &&
      (detailResult || detailError) &&
      !isFetchingDetail
    ) {
      window.CE2.timing.stop('ab_test_result_page');
      perfMetricsStarted.current = false;
    }
  }, [detailError, detailResult, isFetchingDetail]);

  useEffect(() => {
    if (detailError && detailError?.graphQLErrors?.find((error) => error.message === 'Not found')) {
      notifications.error({
        id: 'ab-test-not-found',
        title: 'A/B Test Not Found',
        content: `Couldn't find the A/B Test you were looking for`,
        timeout: 3000,
        skipHoneybadger: true,
      });
      navigate(
        {
          pathname: '/ab-tests',
          search: location.search,
        },
        { replace: true },
      );
    }
  }, [detailError, navigate, location.search, notifications]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value?.special || (value?.start_date && value?.end_date)) {
        paramSet('date', JSON_DATE_RANGE_ADAPTER.serialize(value));
      }
    },
    [paramSet],
  );

  const data = resultData ?? null;
  const metricsLoading = countsEnabled && (!countsData || isFetchingCounts) && !countsError;
  const metricsUnavailable = Boolean(countsError);
  const backTo = new URLSearchParams(location.search).get('back');
  const isBackToGoal = backTo && backTo.startsWith('/goals/');
  const canGoBackToDash = isBackToGoal
    ? permissions.can('navigate', FEATURES.GOALS).allowed
    : permissions.can('navigate', FEATURES.AB_TESTING).allowed;

  const addNewVariant = useCallback(
    (editorPatch) => {
      if (data.type === abTestTypes.SPLIT) {
        modal.show(<AddSplitVariant abTest={data} />);
      } else {
        navigate(
          {
            pathname: `/ab-tests/${id}/variants/new`,
          },
          {
            replace: true,
            state: { editorPatch },
          },
        );
      }
    },
    [data, navigate, id, modal],
  );

  const editVariant = useCallback(
    (abTest, variant) => {
      if (abTest.type === abTestTypes.SPLIT) {
        modal.show(<EditSplitVariant abTest={abTest} variant={variant} />);
      } else {
        navigate(`/ab-tests/${abTest.id}/variants/${variant.id}/edit`);
      }
    },
    [modal, navigate],
  );

  const setView = useCallback(
    (value) => {
      setDataset(value);
      paramSet('view', value);
    },
    [setDataset, paramSet],
  );

  const isDraft = data?.status === AB_TEST_STATUSES.DRAFT;
  const hasNotes = typeof data?.notes === 'string' && data?.notes?.trim?.() !== '';

  const variantsTableWidth = useMemo(() => variantsTableRef.current?.clientWidth ?? null, []);

  if (isDraft) {
    return <Navigate to={`/ab-tests/${id}/edit`} replace={true} />;
  }

  if (detailError && isSharedResourceSubscriptionError(detailError)) {
    return <Navigate replace to="/not-found" />;
  }

  return (
    <DashboardPage>
      <DashHeader
        titleComponent={
          <Breadcrumb>
            {!isSharing && (
              <BreadcrumbItem active={false} to="/ab-tests">
                A/B Testing
              </BreadcrumbItem>
            )}
            {isInitialLoadingDetail || !!data ? (
              <BreadcrumbItem active={true}>
                <div className="mr-5 flex items-center space-x-1">
                  {data?.name ? (
                    <span data-testid="ab-test-name" className="truncate">
                      {data.name}
                    </span>
                  ) : isInitialLoadingDetail ? (
                    <SkeletonLine width="300px" height="16px" />
                  ) : null}
                </div>
              </BreadcrumbItem>
            ) : null}
          </Breadcrumb>
        }
        actionButton={
          <div className="flex flex-shrink-0 items-center space-x-2">
            {canGoBackToDash ? (
              <LinkWithParams className="text-link flex items-center" to={backTo || '/ab-tests'} excluded={['back']}>
                <ArrowIcon className="mr-2.5 h-3 w-3 shrink-0 rotate-180 fill-current" /> Go back to{' '}
                {isBackToGoal ? 'goal' : 'tests'}
              </LinkWithParams>
            ) : null}
            <AbTestActionButton
              entity={data}
              redirectOnDelete="/ab-tests"
              addNewVariant={addNewVariant}
              editVariant={editVariant}
            />
            <CreateAbTestButton />
          </div>
        }
      />

      <div
        className="box-content p-10"
        style={{ minWidth: variantsTableWidth ? `${variantsTableWidth + 2}px` : undefined }}
      >
        <SEO title="A/B Testing Result" />
        {(isFetchingDetail && !data) || isInitialLoadingDetail ? (
          <div className="mt-16 flex w-full items-center justify-center">
            <Spinner />
            <div className="ml-2.5">Loading...</div>
          </div>
        ) : (!isFetchingDetail && !data) || detailError ? (
          <AbTestNotFoundWall />
        ) : (
          <>
            <div className="mb-[15px] flex items-end justify-between">
              <span className="text-body-5">
                {data?.publishedAt ? 'Test published: ' : 'Test created: '}
                {featureStartedAt ? <DateDisplay className="inline" date={featureStartedAt} full={true} /> : 'N/A'}
                <CreatedByText name={data?.publishedBy?.name} textOnly={true} />
              </span>
              <div className="flex items-center space-x-5">
                <Button
                  variant="cancel"
                  onClick={() => modal.show(<AbTestDetailModal data={data} />)}
                  className="!px-0 !text-dodger-blue-500"
                >
                  <InfoIcon className="mr-1.5 h-4 w-4 fill-current" />
                  Test details
                </Button>

                <Button
                  variant="cancel"
                  onClick={() => modal.show(<NotesModal abTest={data} />)}
                  className="!px-0 !text-dodger-blue-500"
                >
                  {hasNotes ? (
                    <NotesAddedIcon className="mr-1.5 h-4 w-4 fill-current" />
                  ) : (
                    <NotesIcon className="mr-1.5 h-4 w-4 fill-current" />
                  )}
                  Notes
                </Button>

                <DateRangePicker
                  startDate={resolvedDateRange?.pickerProps.startDate}
                  endDate={resolvedDateRange?.pickerProps.endDate}
                  lowerBoundary={resolvedDateRange?.pickerProps.lowerBoundary}
                  upperBoundary={resolvedDateRange?.pickerProps.upperBoundary}
                  setCommonDateRange={chooseDateRange}
                  size="lg"
                  special={resolvedDateRange?.pickerProps.special}
                  tooltipOptions={{}}
                  setDateRange={chooseDateRange}
                  showCommonRanges={true}
                  relativeTo={data.stoppedAt}
                  maxRange={resolvedDateRange?.pickerProps.maxRange}
                  timezone={TIMEZONES.UTC}
                  showTimeZone={true}
                />
              </div>
            </div>

            {metricsUnavailable ? (
              <div className="mb-4 flex items-center justify-between rounded border border-radical-red-500 px-4 py-3">
                <span className="text-body-1">Failed to load result metrics for the current goal or date range.</span>
                <Button variant="ghost-primary" onClick={() => refetchCounts?.()}>
                  Try again
                </Button>
              </div>
            ) : null}

            <SummaryRow>
              <SummaryGoalSelector
                goals={goalsList}
                selected={selectedGoal}
                onClick={(goal) => {
                  if (goal.id) {
                    setGoal(goal);
                  }
                }}
                loading={(!isSharing && !goalsData) || isFetchingGoals}
              />
              <SummaryCard
                title="Unique Visitors"
                error={metricsUnavailable}
                loading={metricsLoading}
                value={data?.totalVisitors?.toLocaleString?.() ?? 0}
                onClick={() => {
                  setView('visitors');
                }}
                selected={dataset === 'visitors'}
              />
              <SummaryCard
                title="Unique conversions"
                error={metricsUnavailable}
                loading={metricsLoading}
                value={data?.totalConversions?.toLocaleString?.() ?? 0}
                onClick={() => {
                  setView('conversions');
                }}
                selected={dataset === 'conversions'}
              />
              <SummaryCard
                title="Conversion Rate"
                error={metricsUnavailable}
                loading={metricsLoading}
                value={`${data?.avgConversionRate?.toLocaleString?.() ?? 0}%`}
                unit="avg"
                onClick={() => {
                  setView('conversion_rate');
                }}
                selected={dataset === 'conversion_rate'}
              />
              {/* <SummaryCard title="Avg session worth" value="$6" onClick={() => setDataset('avg_session_worth')} selected={dataset === 'avg_session_worth'} /> */}
              <SummaryCard
                title="Total Conversion Value"
                error={metricsUnavailable}
                loading={metricsLoading}
                value={formatCurrency(valueDisplayWorth(data?.totalValue), valueDisplayCurrency(data?.totalValue), {
                  precision: 10 ** 4,
                })}
                onClick={() => {
                  setView('value');
                }}
                selected={dataset === 'value'}
              />
            </SummaryRow>

            <DistributionChart
              abTest={data}
              goal={goalsList.find((goal) => Number(goal.id) === effectiveSelectedGoalId) ?? selectedGoal ?? null}
              startDate={convertedDateRange?.startDate}
              endDate={convertedDateRange?.endDate}
              timeUnit={convertedDateRange?.timeUnit}
            />
            <VariantsTable
              abTest={data}
              variantsTableRef={variantsTableRef}
              editVariant={editVariant}
              addNewVariant={addNewVariant}
              enableRetiredVariantsAtom={enableRetiredVariantsAtom}
              metricsLoading={metricsLoading}
              metricsUnavailable={metricsUnavailable}
            />
          </>
        )}
      </div>
    </DashboardPage>
  );
}

function VariantsTable({
  abTest,
  variantsTableRef,
  editVariant = null,
  addNewVariant = null,
  enableRetiredVariantsAtom,
  metricsLoading = false,
  metricsUnavailable = false,
}) {
  const modal = useModal();
  const { variants } = abTest;
  const { currentSelection, removeFromSelection, addToSelection, clearSelection, toggleSelection } = useSelection();
  const hasRetiredVariants = useMemo(() => variants.some((v) => v.isRetired), [variants]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRetiredVariants, setShowRetiredVariants] = useAtom(enableRetiredVariantsAtom);
  const permissions = usePermissions();
  const canEditGoals = permissions.can('edit', FEATURES.GOALS).allowed;
  const defaultCurrency = useMemo(() => {
    return getAbTestValueCurrency(abTest, variants);
  }, [abTest, variants]);

  const parsedVariants = useMemo(() => {
    if (!showRetiredVariants) {
      return variants.filter((v) => !v.isRetired);
    }
    return variants;
  }, [variants, showRetiredVariants]);

  const columns = useMemo(() => {
    return [
      {
        id: 'name',
        accessorKey: 'variantName',
        meta: {
          align: 'center',
          justify: 'left',
          padding: '0',
        },
        header: () => (
          <div className="flex items-center space-x-5">
            <div className="pl-3.75">
              <Checkbox
                checked={currentSelection.length && currentSelection.length === parsedVariants.length ? true : false}
                onChange={() => {
                  currentSelection.length === parsedVariants.total
                    ? clearSelection()
                    : parsedVariants.every((x) => currentSelection.find((y) => x.id === y.id))
                      ? removeFromSelection([...parsedVariants])
                      : addToSelection([...parsedVariants]);
                }}
                indeterminate={currentSelection.length > 0 && currentSelection.length < parsedVariants.total}
                title="Toggle all rows selected"
                size="lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <span className="font-bold">Variants</span>
            {hasRetiredVariants ? (
              /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  id="showRetiredVariants"
                  label={<span className="text-body-2 !normal-case">Show retired variants</span>}
                  value={showRetiredVariants}
                  checked={showRetiredVariants}
                  onChange={() =>
                    setShowRetiredVariants((currentValue) => {
                      // if currentSelection contains any retired variants and we toggle this off, remove them
                      const newValue = !currentValue;

                      if (!newValue) {
                        const retiredVariants = currentSelection.filter((v) => v.isRetired);
                        removeFromSelection([...retiredVariants]);
                      }

                      return newValue;
                    })
                  }
                  size="lg"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            ) : null}
          </div>
        ),
        cell({ row }) {
          const selectedIndex = currentSelection.findIndex((x) => x.id === row.original.id);
          const selected = currentSelection.filter((x) => x.id === row.original.id);
          return (
            <div className="flex h-full w-full items-center">
              {selected.length > 0 && <VariantColor position={row.original.position} type={row.original.type} />}
              <div className="ml-5 mr-[14px]">
                <Checkbox
                  checked={selectedIndex > -1}
                  onChange={() => toggleSelection({ ...row.original })}
                  size="lg"
                />
              </div>

              <div
                className={classNames(
                  'group relative mr-6 h-10 w-[65px] overflow-hidden rounded border border-[#E1E6EF] bg-mystic-500 flex-shrink-0',
                  {
                    'cursor-pointer': !!row.original?.screenshotUrl,
                  },
                )}
              >
                <LazyImg
                  className="mr-5 !h-10 w-[65px] rounded !border !border-mystic-500 object-cover"
                  alt="variant thumbnail"
                  Placeholder={ImgPlaceholderIcon}
                  src={row.original?.screenshotUrl ?? null}
                  width="65px"
                  height="40px"
                  onClick={() => {
                    if (!row.original?.screenshotUrl) return;
                    modal.show(<VariantScreenshotModal variant={row.original} />);
                  }}
                />
                {row.original?.screenshotUrl ? (
                  <div className="pointer-events-none absolute bottom-0 right-0 hidden h-10 w-16 items-center justify-center rounded bg-regal-blue-700 bg-opacity-60 border border-[#E1E6EF] group-hover:flex">
                    <EyeIcon className="h-4 w-4 fill-current text-white" />
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col truncate">
                <div className="flex items-center space-x-2.5">
                  <span
                    className={classNames({
                      'text-body-1': !row.original.isRetired,
                      'text-body-3 font-semibold': row.original.isRetired,
                    })}
                  >
                    {row.original.variantName}
                    {row.original.isRetired ? ` (retired)` : ''}
                  </span>
                  {typeof row.original.description === 'string' && row.original.description.trim() !== '' ? (
                    <Tooltip tooltipContent={<div className="max-w-[174px] text-left">{row.original.description}</div>}>
                      <DescriptionIcon className="h-[14px] w-[14px] fill-current text-cadet-blue-500" />
                    </Tooltip>
                  ) : null}
                </div>
                <a
                  data-testid="variant-url-link"
                  className="text-body-2 text-link truncate"
                  href={row.original.viewVariantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={displayUrl(row.original.redirectUrl || abTest.pageUrl)}
                >
                  {displayUrl(row.original.redirectUrl || abTest.pageUrl)}
                </a>
              </div>
            </div>
          );
        },
        size: 300,
      },
      {
        id: 'visitors',
        accessorKey: 'results.visitors',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Unique
            <br />
            Visitors
          </span>
        ),
        cell({ row }) {
          if (metricsUnavailable) return <span className="text-right w-full mr-4">--</span>;
          if (metricsLoading) return <SkeletonLine />;

          return (
            <span className="text-right w-full mr-4">{(row.original.results.visitors || 0).toLocaleString()}</span>
          );
        },
      },
      {
        id: 'conversions',
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Unique
            <br />
            conversions
          </span>
        ),
        accessorKey: 'results.conversions',
        meta: {
          align: 'center',
          justify: 'right',
        },
        cell({ row }) {
          if (metricsUnavailable) return <span className="text-right w-full mr-4">--</span>;
          if (metricsLoading) return <SkeletonLine />;

          return (
            <span className="text-right w-full mr-4">{(row.original.results.conversions || 0).toLocaleString()}</span>
          );
        },
      },
      {
        id: 'conversionRate',
        accessorKey: 'results.conversion_rate',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: ({ column }) => (
          <span
            className={classNames('text-right w-full pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Conversion
            <br />
            Rate
          </span>
        ),
        cell({ row }) {
          if (metricsUnavailable) return <span className="text-right w-full mr-4">--</span>;
          if (metricsLoading) return <SkeletonLine />;

          return (
            <div className="flex flex-col items-center text-right mr-4">
              <span>{(row.original.results.conversionRate || 0).toFixed(2)}%</span>
              <RateDelta value={row.original.results.improvementFromControl} />
            </div>
          );
        },
      },
      {
        id: 'significance',
        accessorKey: 'significance',
        meta: {
          align: 'center',
          justify: 'right',
        },
        header: ({ column }) => (
          <div
            className={classNames('text-right pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            <Tooltip
              tooltipContent={
                <div className="max-w-[250px] text-left">
                  Statistical significance shows how likely your A/B test results are real, not random. 95% confidence
                  or higher is considered significant.
                </div>
              }
            >
              <div className="border-b border-dashed border-cadet-blue-500">
                <div className="text-right">
                  Statistical
                  <br />
                  Significance
                </div>
              </div>
            </Tooltip>
          </div>
        ),
        cell({ row }) {
          if (metricsUnavailable) return <span className="text-right w-full mr-4">--</span>;
          if (metricsLoading) return <SkeletonLine />;

          if (row.original.significance && row.original.significance < 1) {
            return <span className="text-right w-full mr-4">&lt;1%</span>;
          }
          if (row.original.significance) {
            const finalValue = Math.round(row.original.significance);
            const isSignificant = finalValue >= 95;
            return (
              <span
                className={classNames('text-right w-full mr-4', {
                  'font-semibold text-lima-500': isSignificant,
                })}
              >
                {finalValue}%
              </span>
            );
          }
          return <span className="text-right w-full mr-4">0%</span>;
        },
      },
      {
        id: 'value',
        accessorKey: 'results.value.displayWorth',
        meta: {
          align: 'center',
          justify: 'center',
          headerCellClassnames: 'border-l border-mystic-500',
          rowCellClassnames: 'border-l border-mystic-500',
        },
        header: ({ column }) => (
          <span
            className={classNames('text-right pl-5', {
              'pr-4': !column.getIsSorted(),
            })}
          >
            Total Conversion
            <br />
            Value ({defaultCurrency})
          </span>
        ),
        cell({ row }) {
          if (metricsUnavailable) return <span className="text-right w-full mr-4">--</span>;
          if (metricsLoading) return <SkeletonLine />;

          if (abTest.selectedGoal.hasValue || valueDisplayWorth(row.original.results.value) > 0) {
            return (
              <span className="text-right w-full mr-4">
                {formatCurrency(
                  valueDisplayWorth(row.original.results.value),
                  valueDisplayCurrency(row.original.results.value, defaultCurrency),
                  { precision: 10 ** 4 },
                )}
              </span>
            );
          }
          if (!canEditGoals) {
            return <span className="text-body-3 text-right w-full mr-4">Not set</span>;
          }
          return (
            <span className="text-right w-full mr-4">
              <AppLink to={`/goals/${abTest.selectedGoal.id}/edit-value`} className="text-link">
                Set value
              </AppLink>
            </span>
          );
        },
      },
      {
        id: 'actions',
        enableSorting: false,
        minSize: 60,
        maxSize: 60,
        meta: {
          align: 'center',
          justify: 'center',
        },
        cell({ row }) {
          return (
            <VariantActionButton
              abTest={abTest}
              entity={row.original}
              editVariant={editVariant}
              addNewVariant={addNewVariant}
            />
          );
        },
      },
    ];
  }, [
    abTest,
    currentSelection,
    removeFromSelection,
    addToSelection,
    toggleSelection,
    clearSelection,
    parsedVariants,
    showRetiredVariants,
    hasRetiredVariants,
    addNewVariant,
    defaultCurrency,
    editVariant,
    modal,
    setShowRetiredVariants,
    canEditGoals,
    metricsLoading,
    metricsUnavailable,
  ]);

  const tableSortData = useMemo(() => {
    return {
      id: snakeToCamel(searchParams.get('order') || 'NAME'),
      desc: (searchParams.get('sort') || SORT_ORDER_TYPES.ASC) === SORT_ORDER_TYPES.DESC,
    };
  }, [searchParams]);

  const onFetchData = useCallback(
    ({ sorting }) => {
      if (sorting) {
        setSearchParams({
          order: camelToSnake(sorting[0].id).toUpperCase(),
          sort: sorting[0].desc ? SORT_ORDER_TYPES.DESC : SORT_ORDER_TYPES.ASC,
        });
      }
    },
    [setSearchParams],
  );

  return (
    <div
      className="sharp-table-edges overflow-y-auto border border-mystic-500 bg-white rounded-bl rounded-br w-full"
      style={{
        boxShadow: '0 4px 6px 0 rgba(7,31,49,0.07)',
      }}
    >
      <ReactTable
        fetching={false}
        columns={columns}
        data={parsedVariants}
        ref={{ tableRef: variantsTableRef }}
        className="!border-none !rounded-none !shadow-md"
        rowCustomBackground={(row) =>
          classNames({
            'bg-white': row,
          })
        }
        rowCustomClassname={() =>
          classNames({
            'after:!border-solid': true,
          })
        }
        enableSorting={true}
        sorting={tableSortData}
        onFetchData={onFetchData}
      />
    </div>
  );
}

function VariantColor({ position, type }) {
  if (position === -1) {
    return <div className="absolute bottom-px top-0 w-[5px] pb-px">&nbsp;</div>;
  }

  const color = getAbTestVariantColor({ position, type });

  return <div className={classNames('absolute bottom-px top-0 w-[5px] pb-px', color.tw)}>&nbsp;</div>;
}
